/**
 * Arithmetic for paged lists (components/shared/ListPagination): the range
 * label, page counts, the page and size read from a URL, and what one page
 * of a list shows, whether the whole list is in memory (the Templates
 * library, one event type's pipelines) or arrives a page at a time with a
 * cursor per page (the Pipelines list and its search).
 *
 * Pages are counted from 0 here and in the control; the URL counts from 1.
 */

/** "2,211": a count with thousands separators, the same in every locale. */
export const formatCount = (n) => Number(n).toLocaleString('en-US')

/**
 * The words for the rows shown: "51-100 of 2,211", "1-50 of more than 50"
 * while the total is unknown (a search that has not reached the end), and
 * "0 of 0" for an empty list.
 *
 * @param {{from: number, to: number, total: number|null}} range  1-based
 *     first and last row shown (to < from for none), and the total or null
 * @returns {string}
 */
export const rangeLabel = ({ from, to, total }) => {
    const shown = to >= from ? `${formatCount(from)}-${formatCount(to)}` : '0'
    const of =
        total === null || total === undefined
            ? `more than ${formatCount(Math.max(to, from - 1))}`
            : formatCount(total)
    return `${shown} of ${of}`
}

/** How many pages `total` rows fill at `pageSize` a page (at least 1). */
export const pageCountFor = (total, pageSize) =>
    Math.max(1, Math.ceil(total / pageSize))

/**
 * The page asked for in a URL (`?page=3`, counted from 1) as an index from
 * 0. Anything that is not a whole number from 1 up (missing, "abc", "0",
 * "-2", "2.5") is the first page.
 *
 * @param {string|null} raw
 * @returns {number}
 */
export const pageFromParam = (raw) => {
    if (!/^\d+$/.test(String(raw ?? '').trim())) return 0
    const n = Number(raw)
    return n >= 1 ? n - 1 : 0
}

/**
 * The page size asked for in a URL (`?size=60`) when it is one of the
 * offered sizes, else `fallback`.
 *
 * @param {string|null} raw
 * @param {number[]} options
 * @param {number} fallback
 * @returns {number}
 */
export const sizeFromParam = (raw, options, fallback) => {
    const n = Number(raw)
    return options.includes(n) ? n : fallback
}

/**
 * One page of a list held whole in memory. A page past the end shows the
 * last page (a narrowed search, a stale link).
 *
 * @param {{rows: Array, page: number, pageSize: number}} args
 * @returns {{rows: Array, page: number, pageCount: number, from: number,
 *     to: number, total: number, hasNext: boolean}}
 */
export const clientPage = ({ rows, page, pageSize }) => {
    const total = rows.length
    const pageCount = pageCountFor(total, pageSize)
    const shownPage = Math.min(Math.max(0, page), pageCount - 1)
    const start = shownPage * pageSize
    const shown = rows.slice(start, start + pageSize)
    return {
        rows: shown,
        page: shownPage,
        pageCount,
        from: start + 1,
        to: start + shown.length,
        total,
        hasNext: shownPage < pageCount - 1,
    }
}

/**
 * One page of a list read a page at a time with cursors (the Pipelines list
 * and its search, features/pipelineSlice).
 *
 * The total is exact once the last loaded page has no cursor after it (the
 * list was read to its end); before that it is `knownTotal` (the count
 * endpoint) unless that is below what is already loaded (a stale count),
 * and otherwise unknown (null). Pages can be short (the API filters deleted
 * rows after its read limit; a search reads at most 5,000 rows a call), so
 * the range comes from the loaded pages, not from page x size.
 *
 * @param {{pages: Array[], cursors: Array, pageIndex: number,
 *     knownTotal?: number|null}} args
 * @returns {{rows: Array, from: number, to: number, total: number|null,
 *     hasNext: boolean, exhausted: boolean, loaded: number}}
 */
export const cursorPage = ({
    pages,
    cursors,
    pageIndex,
    knownTotal = null,
}) => {
    const rows = pages[pageIndex] ?? []
    const before = pages
        .slice(0, pageIndex)
        .reduce((sum, page) => sum + page.length, 0)
    const loaded = pages.reduce((sum, page) => sum + page.length, 0)
    const exhausted = pages.length > 0 && !cursors[pages.length]
    let total = null
    if (exhausted) total = loaded
    else if (Number.isInteger(knownTotal) && knownTotal >= loaded)
        total = knownTotal
    return {
        rows,
        from: before + 1,
        to: before + rows.length,
        total,
        hasNext: pageIndex < pages.length - 1 || !!cursors[pageIndex + 1],
        exhausted,
        loaded,
    }
}
